import { useState } from "react";
import { Users, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { ProfileSelect } from "@/components/common/ProfileSelect";

export interface AssignableUser {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  display_name?: string | null;
  avatar_url?: string | null;
}

interface BulkReassignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "inspection" | "defect" | "tasks";
  itemCount: number;
  assignableUsers: AssignableUser[];
  onConfirm: (userId: string) => Promise<void>;
}

export function BulkReassignDialog({
  open,
  onOpenChange,
  entityType,
  itemCount,
  assignableUsers,
  onConfirm,
}: BulkReassignDialogProps) {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!selectedUserId) return;

    setIsLoading(true);
    try {
      await onConfirm(selectedUserId);
      onOpenChange(false);
      setSelectedUserId("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedUserId("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("common.bulkReassign.title")}
          </DialogTitle>
          <DialogDescription>
            {t("common.bulkReassign.description", {
              count: itemCount,
              entity:
                entityType === "inspection"
                  ? t("common.bulkReassign.inspections", { count: itemCount })
                  : entityType === "tasks"
                    ? t("common.bulkReassign.tasks", { count: itemCount })
                    : t("common.bulkReassign.defects", { count: itemCount }),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <ProfileSelect
            profiles={assignableUsers.map((u) => ({
              user_id: u.user_id,
              email: u.email,
              first_name: u.first_name,
              last_name: u.last_name,
              display_name: u.display_name || null,
              avatar_url: u.avatar_url || null,
            }))}
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            placeholder={t("common.bulkReassign.selectUser")}
            enableAzureSearch={entityType === "defect"}
            assignmentContext={entityType === "defect" ? "defects" : "inspections"}
            provisionRole="mangelbearbeiter"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedUserId || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("common.bulkReassign.confirm", { count: itemCount })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
